import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert, X } from 'lucide-react';
import { api, jsonBody } from '../api';

interface EmergencyCloseRun {
  id: string;
  status: 'running' | 'completed' | 'attention' | 'interrupted';
  results: Array<{
    accountId: string;
    name: string;
    status: 'flat' | 'attention';
    canceledOrders: number;
    closedPositions: number;
    remainingPositions: number | null;
    pendingOrders: number | null;
    errors: string[];
  }>;
  error: string | null;
}

interface EmergencyClosePreview {
  accounts: Array<{
    accountId: string;
    name: string;
    positions: Array<{ instrumentId: string; side: string; size: string; notionalUsd: string }>;
    pendingOrders: number;
    error: string | null;
  }>;
  skippedAccounts?: Array<{ accountId: string; name: string }>;
  totalPositions: number;
  totalPendingOrders: number;
}

const CLOSE_CONFIRMATION = '停单并平掉全部合约仓位';

export interface ControlView {
  halted: boolean;
  changedAt: string;
  changedBy: string;
  executionEnabled: boolean;
  workerHeartbeat: string | null;
  emergencyCloseRun: EmergencyCloseRun | null;
  telegramControl: { enabled: boolean; healthy: boolean; lastPollAt: string | null };
}

export function TradeControl({ readOnlyContext = false }: { readOnlyContext?: boolean }) {
  const queryClient = useQueryClient();
  const [confirmation, setConfirmation] = useState('');
  const [closeConfirmation, setCloseConfirmation] = useState('');
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const control = useQuery({
    queryKey: ['control'],
    queryFn: () => api<ControlView>('/api/control'),
    refetchInterval: 5_000
  });
  const preview = useQuery({
    queryKey: ['emergency-close-preview'],
    queryFn: () => api<EmergencyClosePreview>('/api/control/close-preview'),
    enabled: closeDialogOpen,
    refetchOnMount: 'always'
  });
  const halt = useMutation({
    mutationFn: () => api('/api/control/halt', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['control'] })
  });
  const resume = useMutation({
    mutationFn: () => api('/api/control/resume', { method: 'POST', ...jsonBody({ confirmation }) }),
    onSuccess: () => {
      setConfirmation('');
      queryClient.invalidateQueries({ queryKey: ['control'] });
    }
  });
  const closeAll = useMutation({
    mutationFn: () => api<EmergencyCloseRun>('/api/control/close-all', {
      method: 'POST', ...jsonBody({ confirmation: closeConfirmation })
    }),
    onSuccess: () => {
      setCloseConfirmation('');
      queryClient.invalidateQueries({ queryKey: ['control'] });
    }
  });
  const data = control.data;
  const workerFresh = data?.workerHeartbeat && Date.now() - new Date(data.workerHeartbeat).getTime() < 90_000;
  const currentRun = data?.emergencyCloseRun?.id === closeAll.data?.id ? data?.emergencyCloseRun :
    closeAll.data ?? data?.emergencyCloseRun;
  const closeRunning = currentRun?.status === 'running';

  return (
    <section className={`trade-control ${data?.halted ? 'halted' : ''}`} aria-live="polite">
      <div className="trade-control-copy">
        <ShieldAlert size={17} />
        <div>
          <strong>{readOnlyContext ? '只读账户模式' : data?.halted ? '全局紧急停单中' : data ? '全局交易控制' : '交易控制状态待确认'}</strong>
          <span>{readOnlyContext ? '仅同步和查看数据；交易操作不可用。' : closeRunning ? '紧急平仓执行中；全局停单保持开启。请查看逐账户执行结果。' :
            data?.emergencyCloseRun?.status === 'attention' || data?.emergencyCloseRun?.status === 'interrupted' ?
              '上次紧急平仓未全部确认完成，请到 OKX 核查。' :
            data?.halted ? '所有新订单已被程序拦截；已有订单和持仓请在 OKX 核查。' :
            data ? `订单执行${data.executionEnabled ? '已开启' : '未开启'} · 策略任务${workerFresh ? '正常' : '离线'}${data.telegramControl?.enabled ? ` · Telegram 控制${data.telegramControl.healthy ? '已连接' : '未连接'}` : ''}` :
            control.error?.message ?? '正在读取数据库控制状态…'}</span>
        </div>
      </div>
      {data && !readOnlyContext ? <div className="trade-control-actions">
        {data.halted ? <>
          <input aria-label="恢复交易确认" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} placeholder="输入“恢复交易”" />
          <button className="secondary-button" disabled={confirmation !== '恢复交易' || resume.isPending || closeRunning} onClick={() => resume.mutate()}>恢复交易</button>
        </> : <button className="trade-halt-button" disabled={halt.isPending} onClick={() => halt.mutate()}>
          {halt.isPending ? '停单中…' : '紧急停单'}
        </button>}
        <button className="trade-close-button" disabled={!data.executionEnabled && !data.emergencyCloseRun} onClick={() => {
          closeAll.reset();
          setCloseDialogOpen(true);
        }}>{closeRunning ? '查看平仓进度' : '紧急平仓'}</button>
      </div> : null}
      {halt.error || resume.error ? <span className="trade-control-error">{halt.error?.message ?? resume.error?.message}</span> : null}
      {closeDialogOpen && !readOnlyContext ? <div className="close-overlay">
        <section className="close-dialog" role="dialog" aria-modal="true" aria-labelledby="close-dialog-title">
          <div className="close-dialog-head">
            <div><span>EMERGENCY / CLOSE ALL</span><h2 id="close-dialog-title">紧急平掉全部合约仓位</h2></div>
            <button className="icon-button" aria-label="关闭平仓窗口" onClick={() => setCloseDialogOpen(false)}><X size={18} /></button>
          </div>
          <p className="close-dialog-warning">覆盖所有可交易账户的全部 SWAP 持仓，包括非策略币种；只读账户自动跳过。系统会先全局停单、撤销普通 SWAP 挂单，再按 OKX 当前持仓市价平仓。条件单、止盈止损单不自动撤销，完成后仍需到 OKX 核查。</p>
          {preview.isPending ? <p className="close-dialog-muted">正在从 OKX 读取当前持仓与普通挂单…</p> : null}
          {preview.error ? <p className="close-dialog-error">预览读取失败：{preview.error.message}</p> : null}
          {preview.data ? <div className="close-preview">
            <strong>{closeAll.data ? '执行前预览' : '实时预览'} · {preview.data.totalPositions} 个持仓 · {preview.data.totalPendingOrders} 笔普通挂单</strong>
            {preview.data.skippedAccounts?.length ? <p className="close-dialog-muted">只读账户不参与平仓：{preview.data.skippedAccounts.map((account) => account.name).join('、')}</p> : null}
            {preview.data.accounts.some((account) => account.error) ? <p className="close-dialog-error">部分账户预览失败，上述合计不包含这些账户；执行结果仍会逐账户核查。</p> : null}
            {preview.data.accounts.map((account) => <div className="close-preview-account" key={account.accountId}>
              <b>{account.name} <small>{account.accountId}</small></b>
              <span>{account.error ? `读取失败：${account.error}` : `${account.positions.length} 个持仓 · ${account.pendingOrders} 笔挂单`}</span>
              {account.positions.map((position) => <small key={`${position.instrumentId}:${position.side}`}>{position.instrumentId} · {position.side} · {position.size} 张 · 约 {position.notionalUsd} USD</small>)}
            </div>)}
          </div> : null}
          {currentRun ? <div className="close-run">
            <strong>{currentRun.status === 'running' ? '正在执行；请勿重复提交' :
              currentRun.status === 'completed' ? '已复核参与平仓账户为空仓' : '存在未完成项，请到 OKX 核查'}</strong>
            <small>任务 ID：{currentRun.id}</small>
            {currentRun.results.map((result) => <div key={result.accountId}>
              <b>{result.name} · {result.status === 'flat' ? '已确认空仓' : '需核查'}</b>
              <span>撤单 {result.canceledOrders} · 已平仓位 {result.closedPositions} · 剩余仓位 {result.remainingPositions ?? '未知'} · 剩余挂单 {result.pendingOrders ?? '未知'}</span>
              {result.errors.map((error, index) => <small key={index}>{error}</small>)}
            </div>)}
            {currentRun.error ? <p className="close-dialog-error">{currentRun.error}</p> : null}
          </div> : null}
          {!closeRunning && !closeAll.data ? <div className="close-dialog-confirm">
            <label htmlFor="close-confirmation">输入“{CLOSE_CONFIRMATION}”确认真实市价平仓</label>
            <input id="close-confirmation" value={closeConfirmation} onChange={(event) => setCloseConfirmation(event.target.value)} placeholder={CLOSE_CONFIRMATION} autoComplete="off" />
            <button className="danger-button" disabled={closeConfirmation !== CLOSE_CONFIRMATION || closeAll.isPending || !preview.data?.accounts.length} onClick={() => closeAll.mutate()}>
              {closeAll.isPending ? '正在启动…' : '确认停单并平仓'}
            </button>
          </div> : null}
          {closeAll.error ? <p className="close-dialog-error">{closeAll.error.message}</p> : null}
        </section>
      </div> : null}
    </section>
  );
}
